import axios from 'axios';
import { API_BASE_URL, getAuthToken } from '../utils/api';

// Create axios instance for quotes API
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Types for quotes system
export interface Quote {
  id: string;
  quoteNumber: string;
  companyId: string;
  clientId: string;
  jobId?: string;
  jobTitle: string;
  description?: string;
  amount: number;
  vatAmount: number;
  totalAmount: number;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED' | 'CHANGES_REQUESTED';
  publicToken?: string | null;
  pdfUrl?: string | null;
  requiredSkills?: string[];
  siteAddress?: string | null;
  sitePostcode?: string | null;
  clientMessage?: string | null;
  approvedByClientName?: string | null;
  validUntil: string;
  sentAt?: string;
  viewedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  expiredAt?: string;
  notes?: string;
  terms?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  // Populated fields from joins
  client?: {
    id: string;
    name: string;
    company?: string;
    email: string;
    phone?: string;
  };
  items?: QuoteItem[];
}

export interface QuoteItem {
  id: string;
  quoteId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  itemType: 'SERVICE' | 'MATERIAL' | 'LABOUR' | 'CALLOUT';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateQuoteRequest {
  clientId: string;
  jobId?: string;
  jobTitle: string;
  description?: string;
  validUntil: string;
  notes?: string;
  terms?: string;
  requiredSkills?: string[];
  siteAddress?: string;
  sitePostcode?: string;
  items: Omit<QuoteItem, 'id' | 'quoteId' | 'createdAt' | 'updatedAt'>[];
}

export interface QuoteEvent {
  id: string;
  quoteId: string;
  eventType: string;
  userId: string | null;
  meta: any;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface UpdateQuoteRequest {
  jobTitle?: string;
  description?: string;
  validUntil?: string;
  notes?: string;
  terms?: string;
  items?: Omit<QuoteItem, 'id' | 'quoteId' | 'createdAt' | 'updatedAt'>[];
}

export interface QuoteStatusUpdate {
  status: Quote['status'];
  notes?: string;
}

// Quotes service with Railway API integration
export const quotesService = {
  // Get all quotes for the current company
  async getQuotes(): Promise<Quote[]> {
    try {
      const response = await api.get('/quotes');
      return response.data.data || response.data || [];
    } catch (error) {
      console.error('Error fetching quotes:', error);
      throw error;
    }
  },

  // Get a single quote by ID
  async getQuote(id: string): Promise<Quote> {
    try {
      const response = await api.get(`/quotes/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching quote:', error);
      throw error;
    }
  },

  // Create a new quote
  async createQuote(quoteData: CreateQuoteRequest): Promise<Quote> {
    try {
      const response = await api.post('/quotes', quoteData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error creating quote:', error);
      throw error;
    }
  },

  // Update an existing quote
  async updateQuote(id: string, quoteData: UpdateQuoteRequest): Promise<Quote> {
    try {
      const response = await api.patch(`/quotes/${id}`, quoteData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  },

  // Update quote status
  async updateQuoteStatus(id: string, statusUpdate: QuoteStatusUpdate): Promise<Quote> {
    try {
      const response = await api.patch(`/quotes/${id}/status`, statusUpdate);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error updating quote status:', error);
      throw error;
    }
  },

  // Delete a quote
  async deleteQuote(id: string): Promise<void> {
    try {
      await api.delete(`/quotes/${id}`);
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  },

  // Convert quote to job (auto-assigns engineer on backend)
  async convertToJob(id: string): Promise<{ jobId: string; engineerId: string | null }> {
    try {
      const response = await api.post(`/quotes/${id}/convert-to-job`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error converting quote to job:', error);
      throw error;
    }
  },

  // Get quote activity timeline (SENT, OPENED, APPROVED, etc.)
  async getQuoteEvents(id: string): Promise<QuoteEvent[]> {
    try {
      const response = await api.get(`/quotes/${id}/events`);
      return response.data.data || response.data || [];
    } catch (error) {
      console.error('Error fetching quote events:', error);
      throw error;
    }
  },

  // Send quote to client
  async sendQuote(id: string): Promise<Quote> {
    try {
      const response = await api.post(`/quotes/${id}/send`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error sending quote:', error);
      throw error;
    }
  },

  // Get quote PDF
  async getQuotePDF(id: string): Promise<Blob> {
    try {
      const response = await api.get(`/quotes/${id}/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error getting quote PDF:', error);
      throw error;
    }
  },

  // Get quotes by status
  async getQuotesByStatus(status: Quote['status']): Promise<Quote[]> {
    try {
      const response = await api.get(`/quotes?status=${status}`);
      return response.data.data || response.data || [];
    } catch (error) {
      console.error('Error fetching quotes by status:', error);
      throw error;
    }
  },

  // Get quotes by client
  async getQuotesByClient(clientId: string): Promise<Quote[]> {
    try {
      const response = await api.get(`/quotes?clientId=${clientId}`);
      return response.data.data || response.data || [];
    } catch (error) {
      console.error('Error fetching quotes by client:', error);
      throw error;
    }
  },

  // Get quote statistics
  async getQuoteStats(): Promise<{
    total: number;
    draft: number;
    sent: number;
    accepted: number;
    rejected: number;
    expired: number;
    totalValue: number;
    acceptanceRate: number;
  }> {
    try {
      const response = await api.get('/quotes/stats');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching quote stats:', error);
      throw error;
    }
  }
};

export default quotesService;
